import { useQuery, useQueries } from 'react-query';

import type { ILead } from 'src/services/api/leads/types';
import { apiClient } from 'src/services/api/client';
import { queryKeys } from 'src/services/api/constants';
import { GET_LEADS_PATH } from 'src/services/api/leads/constants';

type IGetLeadResponse = {
    lead: ILead;
};

const fetchLead = async (leadUuid?: string): Promise<ILead> => {
    if (!leadUuid) {
        throw new Error('Lead UUID is required to fetch lead data');
    }

    const path = `${GET_LEADS_PATH}/${leadUuid}`;
    const res = await apiClient(path).json<IGetLeadResponse>();
    return res.lead;
};

/**
 * Fetch data about a single lead using its UUID.
 * Ideally we hit the cache if we already have the lead data (e.g. from a prior query for useLeads which bulk-fetches leads).
 */
export const useLead = (leadUuid?: string) => {
    return useQuery<ILead>(
        [queryKeys.leads, leadUuid],
        async () => fetchLead(leadUuid),
        { enabled: !!leadUuid }
    );
};

/**
 * Fetch multiple leads by their UUIDs using useQueries.
 * Returns a Record mapping UUIDs to ILead objects for easy lookup.
 * Uses the same query keys as useLead to share cache data.
 */
export const useLeadsByUuids = (leadUuids: string[]) => {
    const queries = useQueries(
        leadUuids.map((leadUuid) => ({
            queryKey: [queryKeys.leads, leadUuid],
            queryFn: async () => fetchLead(leadUuid),
        }))
    );

    const leadsByUuid: Record<string, ILead> = {};
    queries.forEach((query, index) => {
        if (query.data && leadUuids[index]) {
            leadsByUuid[leadUuids[index]] = query.data;
        }
    });

    return leadsByUuid;
};